import { useState, useEffect } from 'react';
import { X, Download, Share2, MoreHorizontal } from 'lucide-react';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

export function PWAInstallBanner() {
    const { t } = useTranslation();
    const { isInstallable, isInstalled, isInAppBrowser, handleInstall } = usePWAInstall();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Show banner after a short delay if it's installable or in an in-app browser
        // and not already installed.
        if (!isInstalled && (isInstallable || isInAppBrowser)) {
            const timer = setTimeout(() => {
                // Check if user has already dismissed it in this session
                const dismissed = sessionStorage.getItem('pwa_banner_dismissed');
                if (!dismissed) {
                    setIsVisible(true);
                }
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [isInstallable, isInstalled, isInAppBrowser]);

    const handleDismiss = () => {
        setIsVisible(false);
        sessionStorage.setItem('pwa_banner_dismissed', 'true');
    };

    if (!isVisible) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                className="fixed bottom-4 left-4 right-4 z-[9999] md:left-auto md:right-4 md:w-96"
            >
                <div className="bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-800 p-4 relative overflow-hidden">
                    {/* Progress background effect */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-blue-500/20">
                        <motion.div
                            className="h-full bg-blue-500"
                            initial={{ width: "0%" }}
                            animate={{ width: "100%" }}
                            transition={{ duration: 3, ease: "linear" }}
                        />
                    </div>

                    <button
                        onClick={handleDismiss}
                        className="absolute top-3 right-3 text-slate-400 hover:text-white transition-colors"
                    >
                        <X size={18} />
                    </button>

                    <div className="flex gap-4 items-center">
                        <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                            <Download className="text-white" size={24} />
                        </div>

                        <div className="flex-1 pr-6">
                            <h3 className="font-bold text-sm text-white">
                                {t('pwa.install_title', 'Installer PadelPartner')}
                            </h3>
                            <p className="text-xs text-slate-400 leading-tight mt-1">
                                {isInAppBrowser
                                    ? t('pwa.in_app_description', 'Pour une meilleure expérience, ouvrez dans votre navigateur habituel.')
                                    : t('pwa.install_description', 'Accédez plus rapidement à vos réservations depuis votre écran d\'accueil.')
                                }
                            </p>
                        </div>
                    </div>

                    <div className="mt-4 flex flex-col gap-2">
                        {isInAppBrowser ? (
                            <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50">
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-blue-400 mb-2">
                                    {t('pwa.how_to_install', 'Comment installer :')}
                                </p>
                                <div className="space-y-2">
                                    <div className="flex items-start gap-2 text-xs">
                                        <div className="w-5 h-5 bg-slate-700 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">1</div>
                                        <p className="flex items-center gap-1 flex-wrap">
                                            {t('pwa.step1', 'Appuyez sur')} <MoreHorizontal size={14} className="inline bg-slate-700 rounded p-0.5" /> {t('pwa.step1_suffix', 'ou')} <Share2 size={14} className="inline bg-slate-700 rounded p-0.5" />
                                        </p>
                                    </div>
                                    <div className="flex items-start gap-2 text-xs">
                                        <div className="w-5 h-5 bg-slate-700 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">2</div>
                                        <p>{t('pwa.step2', 'Sélectionnez "Ouvrir dans Chrome" ou "Ajouter à l\'écran d\'accueil"')}</p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={handleInstall}
                                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 rounded-xl transition-all active:scale-95 flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20"
                            >
                                <Download size={18} />
                                {t('pwa.install_button', 'Installer maintenant')}
                            </button>
                        )}
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
